/* eslint-disable no-undef */
import { Icon } from "@iconify/react";
import Tooltip from "@mui/material/Tooltip";
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";

export type Action = {
  name: string;
  disabled?: boolean;
  tooltipText?: string;
  type?: string;
  maskPiiData?: boolean;
};

const MENU_WIDTH = 176; // matches tailwind w-44
const VIEWPORT_MARGIN = 8;
const MENU_GAP = 8;
const AVG_OPTION_HEIGHT = 36;
const MENU_HEADER_HEIGHT = 32;

export function CTADropdown({
  actions,
  onAction,
  className = "items-end",
  dropdownClassName = "",
  render = () => (
    <Icon
      icon="carbon:overflow-menu-horizontal"
      className="text-gray-400 rounded hover:bg-gray-300"
      height={20}
    />
  ),
}: {
  className?: string;
  dropdownClassName?: string;
  actions: Action[];
  onAction: (action: Action) => void;
  render?: () => JSX.Element;
}) {
  const [dropdownOpen, setDropDownOpen] = useState(false);
  const [position, setPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const activatedByPointerRef = useRef(false);

  // The dropdown is rendered through a portal (see below) so it always paints
  // above the AG Grid rows/columns it's triggered from, instead of being
  // clipped or overpainted by whichever grid row/column DOM node happens to
  // come later in document order.
  useLayoutEffect(() => {
    if (!dropdownOpen || !buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const menuHeight =
      MENU_HEADER_HEIGHT + (actions || []).length * AVG_OPTION_HEIGHT;
    const openUp =
      rect.bottom + MENU_GAP + menuHeight > window.innerHeight &&
      rect.top - MENU_GAP - menuHeight > 0;
    const left = Math.min(
      Math.max(rect.right - MENU_WIDTH, VIEWPORT_MARGIN),
      window.innerWidth - MENU_WIDTH - VIEWPORT_MARGIN
    );
    const top = openUp
      ? rect.top - MENU_GAP - menuHeight
      : rect.bottom + MENU_GAP;
    setPosition({ top, left });
  }, [dropdownOpen, actions]);

  useEffect(() => {
    if (!dropdownOpen) return undefined;
    const close = () => setDropDownOpen(false);
    // capture:true so this also fires for scrolling inside AG Grid's own
    // scroll containers, which don't bubble a "scroll" event to window.
    document.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      document.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [dropdownOpen]);

  useEffect(() => {
    const checkIfClickOutside = (e: any) => {
      if (!dropdownOpen) return;
      const isInsideTrigger = buttonRef.current?.contains(e.target);
      const isInsideMenu = menuRef.current?.contains(e.target);
      if (!(isInsideTrigger || isInsideMenu)) {
        setDropDownOpen(false);
      }
    };
    document.addEventListener("mousedown", checkIfClickOutside);
    return () => {
      document.removeEventListener("mousedown", checkIfClickOutside);
    };
  }, [dropdownOpen]);

  const RenderElement = render;
  if (!actions) {
    return null;
  }
  return (
    <div className={`flex flex-col ${className} `}>
      <button
        ref={buttonRef}
        className="inline-flex text-xs text-center rounded-lg peer"
        type="button"
        onPointerDown={() => {
          activatedByPointerRef.current = true;
          setDropDownOpen((state) => !state);
        }}
        onPointerUp={() => {
          // Native click is dispatched immediately after pointerup. Clear the
          // flag on the next task so that click is ignored, but a cancelled
          // click does not affect the next interaction.
          window.setTimeout(() => {
            activatedByPointerRef.current = false;
          }, 0);
        }}
        onPointerCancel={() => {
          activatedByPointerRef.current = false;
        }}
        onClick={() => {
          if (activatedByPointerRef.current) return;
          setDropDownOpen((state) => !state);
        }}
      >
        <RenderElement />
      </button>
      {dropdownOpen &&
        position &&
        ReactDOM.createPortal(
          <div
            id="dropdown"
            ref={menuRef}
            className={`fixed z-50 bg-white border divide-y divide-gray-100 rounded shadow ${dropdownClassName}`}
            style={{
              top: position.top,
              left: position.left,
              width: MENU_WIDTH,
            }}
          >
            <p className="px-4 py-1 text-stone-300">More actions</p>
            <ul
              className="py-1 text-xs font-normal divide-y text-dark"
              aria-labelledby="dropdownDefault"
            >
              {actions.map((action, index) => (
                <li key={index}>
                  <Tooltip
                    title={
                      action.disabled && action.tooltipText
                        ? action.tooltipText
                        : ""
                    }
                  >
                    <span>
                      <a
                        href=""
                        className={` block px-4 py-2 hover:bg-slate-50  ${
                          action.disabled
                            ? "text-gray-300 cursor-not-allowed "
                            : "hover:text-red-500"
                        } `}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={(e) => {
                          e.preventDefault();
                          if (action.disabled) return;
                          onAction(action);
                          setDropDownOpen((state) => !state);
                        }}
                        aria-disabled={action.disabled}
                      >
                        {action.name}
                      </a>
                    </span>
                  </Tooltip>
                </li>
              ))}
            </ul>
          </div>,
          document.body
        )}
    </div>
  );
}
